import { GREY, STANDE, brand } from "../standeTokens";

/** A session's state, in the words and colours the space uses everywhere. */
export const STATE = {
  LIVE: { label: "Live now", board: "Live now", dot: STANDE.darkRed, tint: "lightRed" },
  SCHEDULED: { label: "Scheduled", board: "Scheduled", dot: STANDE.primaryBlue, tint: null },
  DRAFT: { label: "Draft", board: "Drafts", dot: GREY[400], tint: null },
  DONE: { label: "Done", board: "Done", dot: STANDE.darkGreen, tint: "lightGreen" },
};

export const stateTint = (state, isDark) => {
  const key = STATE[state]?.tint;
  return key ? brand(isDark)[key] : null;
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A time from the server, as a Date.
 *
 * The server keeps times in UTC and sends them without a zone
 * ("2026-10-09T08:00:00"), which a browser would read as its own local
 * time — moving every session by the reader's offset. So a time without a
 * zone is read as UTC; one that carries a zone is left as it is.
 */
export const serverTime = (v) => {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const zoned = /(Z|[+-]\d{2}:?\d{2})$/.test(v);
  const d = new Date(zoned ? v : `${v}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const valid = serverTime;

/** "Fri 9 Oct" */
export const dayLabel = (v) => {
  const d = valid(v);
  return d ? `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}` : null;
};

/** The parts of a date box: weekday, day of month, month. */
export const dateParts = (v) => {
  const d = valid(v);
  return d ? { weekday: DAYS[d.getDay()], day: d.getDate(), month: MONTHS[d.getMonth()] } : null;
};

/** "09:00" — a 24-hour clock, as timetables are written. */
export const clock = (v) => {
  const d = valid(v);
  return d ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : null;
};

/** "50 min", "1 h 30 min" */
export const duration = (minutes) => {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`;
};

/** "Fri 9 Oct · 09:00 · 50 min", leaving out what is not known. */
export const whenLine = (session) =>
  [dayLabel(session.scheduled_at), clock(session.scheduled_at), duration(session.duration_minutes)]
    .filter(Boolean)
    .join(" · ");

/** What the questions before class amount to, in a phrase. */
export const questionsLine = (session, isInstructor) => {
  if (!session.questions_enabled) return "Questions off";
  if (!isInstructor) return "Questions open";
  const n = session.pre_class_question_count || 0;
  const open = session.pre_class_unanswered_count || 0;
  if (!n) return "Questions open · nothing asked yet";
  return `${n} ${n === 1 ? "question" : "questions"} asked${open ? ` · ${open} unanswered` : ""}`;
};

export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
